import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Chip, TablePagination } from '@material-ui/core';
import ArrowDownward from '@material-ui/icons/ArrowDropDown';
import ArrowRight from '@material-ui/icons/ArrowRight';
import DeleteIcon from '@material-ui/icons/Cancel';
import DoneIcon from '@material-ui/icons/Done';
import RefreshIcon from '@material-ui/icons/Refresh';

import { CommunicationMessageTypes } from '../../helpers/constants';
import { LogItem } from '../../helpers/logger';
import useCommunicator from '../../hooks/useCommunicator';
import { ILogControlMessage } from '../../types/communicator-types';

interface IFilters {
  logs: Array<LogItem>;
  afterFilter: (logs: Array<LogItem>) => void;
}

const Container = styled.div`
  background: #0000ffa1;
  padding: 5px;
  border-radius: 5px;
  svg.refresh {
    cursor: pointer;
    position: relative;
    top: 8px;
  }
`;

const Heading = styled.h3`
  margin-right: 5px;
  display: inline-block;
`;

const SubHeading = styled.h3`
  display: inline-block;
  margin-right: 5px;
`;

const Filters: React.FC<IFilters> = (props) => {
  const { logs, afterFilter } = props;
  const [columns, setColumns] = useState<Array<String>>([]);
  const [tags, setTags] = useState<Array<String>>([]);
  const [inactiveColumns, setInactiveColumns] = useState({});
  const [inactiveTags, setInactiveTags] = useState({});
  const [logsPerPage, setLogsPerPage] = useState(100);
  const [page, setPage] = useState(0);
  const [filteredLogs, setFilteredLogs] = useState<Array<LogItem>>([]);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const logCommunicator = useCommunicator<ILogControlMessage>(
    CommunicationMessageTypes.LogControl
  );

  useEffect(() => {
    if (logs.length > 1) {
      const columnSet = new Set(logs.map((item) => item.columns).flat());
      const sortedColumns = Array.from(columnSet).sort((a, b) =>
        a > b ? -1 : 1
      );
      setColumns(sortedColumns);

      const tagSet = new Set(logs.map((item) => item.tags).flat());
      const sortedTags = Array.from(tagSet).sort((a, b) => (a > b ? -1 : 1));
      setTags(sortedTags);
    } else {
      setColumns([]);
      setTags([]);
    }
  }, [logs]);
  useEffect(() => {
    const filtered = logs
      .filter((log) => {
        if (log.tags.length === 0) {
          return true;
        }
        const activeTags = log.tags.filter(
          (tag) => !inactiveTags[tag.toString()]
        );
        return activeTags.length > 0;
      })
      .filter((log) => {
        const activeColumns = log.columns.filter(
          (column) => !inactiveColumns[column.toString()]
        );
        return activeColumns.length > 0;
      })
      .sort((a, b) => b.timeStamp - a.timeStamp);
    const sliced = filtered.slice(
      page * logsPerPage,
      page * logsPerPage + logsPerPage
    );
    setFilteredLogs(filtered);
    afterFilter(sliced);
  }, [logs, inactiveColumns, inactiveTags, logsPerPage, page]);
  return (
    <Container>
      <div>
        {!preferencesOpen && (
          <ArrowRight
            style={{ cursor: 'pointer' }}
            onClick={() => setPreferencesOpen(true)}
          />
        )}
        {preferencesOpen && (
          <ArrowDownward
            style={{ cursor: 'pointer' }}
            onClick={() => setPreferencesOpen(false)}
          />
        )}

        <Heading>Filters and stuff</Heading>
        <RefreshIcon
          onClick={() => {
            logCommunicator?.postMessage({ type: 'LogRequest' });
          }}
          className="refresh"
        />
      </div>
      {preferencesOpen && (
        <>
          <div>
            <SubHeading>Columns:</SubHeading>
            {columns.map((column) => (
              <FilterItem
                key={column}
                name={column}
                active={!inactiveColumns[column.toString()]}
                onClick={(newValue) => {
                  setInactiveColumns((active) => ({
                    ...active,
                    [column.toString()]: newValue,
                  }));
                }}
                deleteIcon={<DoneIcon />}
              />
            ))}
          </div>
          <div>
            <SubHeading>Tags:</SubHeading>
            {tags.map((tag) => (
              <FilterItem
                key={tag}
                name={tag}
                active={!inactiveTags[tag.toString()]}
                onClick={(newValue) => {
                  setInactiveTags((active) => ({
                    ...active,
                    [tag.toString()]: newValue,
                  }));
                }}
                deleteIcon={<DoneIcon />}
              />
            ))}
          </div>
          <div>
            <TablePagination
              component="div"
              count={filteredLogs.length}
              rowsPerPageOptions={[
                10,
                50,
                100,
                200,
                { value: -1, label: 'All' },
              ]}
              page={page}
              onPageChange={(_, _page) => {
                setPage(_page);
              }}
              rowsPerPage={logsPerPage}
              onRowsPerPageChange={(evt) => {
                setLogsPerPage(+evt.target.value);
              }}
            />
          </div>
        </>
      )}
    </Container>
  );
};

interface IFliterItem {
  name: string;
  active: boolean;
  onClick: (newValue: boolean) => void;
}

const FilterItem: React.FC<any> = (props: IFliterItem) => {
  const { name, active, onClick } = props;
  return (
    <Chip
      color={active ? 'primary' : 'default'}
      label={name}
      onClick={() => onClick(active)}
      icon={active ? <DoneIcon /> : <DeleteIcon />}
    />
  );
};

export default Filters;
